/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ExplainResourceAccessRequest,
  ExplainResourceAccessSecurity,
  ResourceKind,
} from "../models/operations/explainresourceaccess.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildExplainResourceAccessQuery,
  ExplainResourceAccessQueryData,
  prefetchExplainResourceAccess,
  queryKeyExplainResourceAccess,
} from "./explainResourceAccess.core.js";
export {
  buildExplainResourceAccessQuery,
  type ExplainResourceAccessQueryData,
  prefetchExplainResourceAccess,
  queryKeyExplainResourceAccess,
};

export type ExplainResourceAccessQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * explainResourceAccess access
 *
 * @remarks
 * Explain whether one organization member can connect to, view, and manage one resource, and which rules decide it. The decision comes from the same evaluation as runtime enforcement. A gateway is refused: nothing checks access on its own id, so check each server it fronts instead. Like listIdentityAccess it describes one person's access, so it takes a session only: API keys are not checked against grants and would see any member's rules.
 */
export function useExplainResourceAccess(
  request: ExplainResourceAccessRequest,
  security?: ExplainResourceAccessSecurity | undefined,
  options?: QueryHookOptions<
    ExplainResourceAccessQueryData,
    ExplainResourceAccessQueryError
  >,
): UseQueryResult<
  ExplainResourceAccessQueryData,
  ExplainResourceAccessQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildExplainResourceAccessQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * explainResourceAccess access
 *
 * @remarks
 * Explain whether one organization member can connect to, view, and manage one resource, and which rules decide it. The decision comes from the same evaluation as runtime enforcement. A gateway is refused: nothing checks access on its own id, so check each server it fronts instead. Like listIdentityAccess it describes one person's access, so it takes a session only: API keys are not checked against grants and would see any member's rules.
 */
export function useExplainResourceAccessSuspense(
  request: ExplainResourceAccessRequest,
  security?: ExplainResourceAccessSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ExplainResourceAccessQueryData,
    ExplainResourceAccessQueryError
  >,
): UseSuspenseQueryResult<
  ExplainResourceAccessQueryData,
  ExplainResourceAccessQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildExplainResourceAccessQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setExplainResourceAccessData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      resourceKind: ResourceKind;
      resourceId: string;
      userId: string;
      gramSession?: string | undefined;
    },
  ],
  data: ExplainResourceAccessQueryData,
): ExplainResourceAccessQueryData | undefined {
  const key = queryKeyExplainResourceAccess(...queryKeyBase);

  return client.setQueryData<ExplainResourceAccessQueryData>(key, data);
}

export function invalidateExplainResourceAccess(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      resourceKind: ResourceKind;
      resourceId: string;
      userId: string;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "explainResourceAccess",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllExplainResourceAccess(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "explainResourceAccess"],
  });
}
